(() => {
  const clamp = (value, minimum = 0, maximum = 1) => Math.max(minimum, Math.min(maximum, value));

  const initRitual = (section) => {
    if (section.dataset.luunimaInitialized === 'true') return;

    const journey = section.querySelector('[data-luunima-journey]');
    const svg = section.querySelector('.luunima-ritual__svg');
    const cursor = section.querySelector('[data-luunima-cursor]');
    const stations = [...section.querySelectorAll('[data-luunima-station]')];
    const mobileGuidePath = section.querySelector('[data-luunima-guide-mobile]');
    const desktopGuidePath = section.querySelector('[data-luunima-guide-desktop]');
    const mobileDrawPath = section.querySelector('[data-luunima-draw-mobile]');
    const desktopDrawPath = section.querySelector('[data-luunima-draw-desktop]');
    const triggers = [...section.querySelectorAll('[data-luunima-note-trigger]')];

    if (!journey || !svg || !stations.length || !mobileGuidePath || !desktopGuidePath || !mobileDrawPath || !desktopDrawPath) return;

    section.dataset.luunimaInitialized = 'true';
    const fallbackMobile = mobileGuidePath.getAttribute('d') || '';
    const fallbackDesktop = desktopGuidePath.getAttribute('d') || '';
    let frame = 0;
    let hoverTimer = 0;
    let pinnedTrigger = null;
    let renderedProgress = null;
    let previousFrameTime = 0;

    const closeAllNotes = () => {
      section.querySelectorAll('[data-luunima-note]').forEach((note) => { note.dataset.open = 'false'; });
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      pinnedTrigger = null;
    };

    const openNote = (trigger, pinned = false) => {
      const noteId = trigger.getAttribute('aria-controls');
      const note = noteId ? document.getElementById(noteId) : null;
      if (!note || !section.contains(note)) return;
      section.querySelectorAll('[data-luunima-note]').forEach((item) => { item.dataset.open = item === note ? 'true' : 'false'; });
      triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      pinnedTrigger = pinned ? trigger : null;
    };

    const revealFallback = () => {
      section.classList.remove('luunima-ritual--animated');
      stations.forEach((station) => {
        station.style.setProperty('--luunima-station-number-visible', '1');
        station.style.setProperty('--luunima-station-content-visible', '1');
      });
      mobileDrawPath.setAttribute('d', fallbackMobile);
      desktopDrawPath.setAttribute('d', fallbackDesktop);
      if (cursor) cursor.style.opacity = '0';
    };

    const drawPrefix = (guidePath, drawPath, progress) => {
      const totalLength = guidePath.getTotalLength();
      if (!Number.isFinite(totalLength) || totalLength <= 0) throw new Error('Ungültige Ritualpfadlänge');
      const p = clamp(progress);
      if (p <= 0.001) {
        drawPath.setAttribute('d', '');
        return;
      }
      const visibleLength = totalLength * p;
      const sampleCount = Math.max(4, Math.min(120, Math.ceil(120 * p)));
      const points = [];
      for (let index = 0; index <= sampleCount; index += 1) {
        const point = guidePath.getPointAtLength(visibleLength * (index / sampleCount));
        points.push(`${index === 0 ? 'M' : 'L'} ${point.x.toFixed(3)} ${point.y.toFixed(3)}`);
      }
      drawPath.setAttribute('d', points.join(' '));
    };

    const setProgress = (timestamp) => {
      const viewport = window.innerHeight || document.documentElement.clientHeight || 1;
      const playhead = viewport * 0.72;
      const stationCenters = stations.map((station) => {
        const rect = station.getBoundingClientRect();
        return rect.top + (rect.height / 2);
      });
      const firstStation = stationCenters[0];
      const lastStation = stationCenters[stationCenters.length - 1];
      const travelDistance = lastStation - firstStation;
      if (!Number.isFinite(firstStation) || !Number.isFinite(lastStation) || travelDistance < 50) throw new Error('Ungültige Ritualgeometrie');

      const targetProgress = clamp((playhead - firstStation) / travelDistance);
      if (renderedProgress === null) renderedProgress = targetProgress;
      const elapsed = previousFrameTime ? Math.min(48, Math.max(1, timestamp - previousFrameTime)) : 16.67;
      previousFrameTime = timestamp;
      const interpolation = 1 - Math.pow(0.72, elapsed / 16.67);
      renderedProgress += (targetProgress - renderedProgress) * interpolation;
      if (Math.abs(targetProgress - renderedProgress) < 0.0004) renderedProgress = targetProgress;
      const cursorProgress = clamp(renderedProgress);
      const stationThresholds = stationCenters.map((center) => clamp((center - firstStation) / travelDistance));

      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const activeGuide = desktop ? desktopGuidePath : mobileGuidePath;
      const activeDraw = desktop ? desktopDrawPath : mobileDrawPath;
      const inactiveDraw = desktop ? mobileDrawPath : desktopDrawPath;
      inactiveDraw.setAttribute('d', '');
      drawPrefix(activeGuide, activeDraw, cursorProgress);

      stations.forEach((station, index) => {
        const threshold = stationThresholds[index] ?? (index / Math.max(1, stations.length - 1));
        const revealStart = Math.max(0, threshold - 0.018);
        const numberVisible = clamp((cursorProgress - revealStart) * 52);
        const contentVisible = clamp((cursorProgress - revealStart) * 42);
        station.style.setProperty('--luunima-station-number-visible', numberVisible.toFixed(3));
        station.style.setProperty('--luunima-station-content-visible', contentVisible.toFixed(3));
      });

      if (cursor) {
        const length = activeGuide.getTotalLength();
        const point = activeGuide.getPointAtLength(length * cursorProgress);
        const svgRect = svg.getBoundingClientRect();
        const journeyRect = journey.getBoundingClientRect();
        const x = (svgRect.left - journeyRect.left) + (point.x / 100) * svgRect.width;
        const y = (svgRect.top - journeyRect.top) + (point.y / 100) * svgRect.height;
        if (!Number.isFinite(x) || !Number.isFinite(y)) throw new Error('Ungültige Cursorposition');
        cursor.style.left = `${x}px`;
        cursor.style.top = `${y}px`;
        cursor.style.opacity = cursorProgress > 0.002 ? '1' : '0';
      }

      section.classList.add('luunima-ritual--animated');
      return Math.abs(targetProgress - renderedProgress) >= 0.0004;
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        try {
          if (setProgress(performance.now())) requestUpdate();
        } catch (error) {
          console.warn('LUUNIMA ritual animation fallback', error);
          revealFallback();
        }
      });
    };

    const pageWrapper = document.querySelector('.page-wrapper');
    window.addEventListener('scroll', requestUpdate, { passive: true });
    document.addEventListener('scroll', requestUpdate, { passive: true });
    if (pageWrapper) pageWrapper.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    requestUpdate();
    window.setTimeout(requestUpdate, 80);
    window.setTimeout(requestUpdate, 350);

    triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => {
        if (!window.matchMedia('(hover: hover)').matches || pinnedTrigger) return;
        window.clearTimeout(hoverTimer);
        openNote(trigger);
      });
      trigger.addEventListener('pointerleave', () => {
        if (!window.matchMedia('(hover: hover)').matches || pinnedTrigger) return;
        hoverTimer = window.setTimeout(closeAllNotes, 130);
      });
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.clearTimeout(hoverTimer);
        if (pinnedTrigger === trigger) closeAllNotes();
        else openNote(trigger, true);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { closeAllNotes(); trigger.focus(); }
      });
    });

    document.addEventListener('pointerdown', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-luunima-note-trigger], [data-luunima-note]')) return;
      closeAllNotes();
    });
  };

  const initStaggeredReveal = (scope, selector, initializedKey) => {
    const items = [...scope.querySelectorAll(selector)].filter((item) => !item.dataset[initializedKey]);
    if (!items.length) return;
    items.forEach((item) => { item.dataset[initializedKey] = 'true'; });
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const item = entry.target;
        const index = items.indexOf(item);
        observer.unobserve(item);
        window.setTimeout(() => item.classList.add('is-visible'), index * 55);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -12% 0px' });
    items.forEach((item) => observer.observe(item));
  };

  const initThemenweltenReveal = (scope = document) => initStaggeredReveal(scope, '[data-luunima-fade-in]', 'luunimaRevealInitialized');
  const initCompanionReveal = (scope = document) => initStaggeredReveal(scope, '[data-luunima-companion]', 'luunimaCompanionInitialized');
  const initImageReveal = (scope = document) => initStaggeredReveal(scope, '.luunima-section img:not([data-luunima-no-fade])', 'luunimaImageRevealInitialized');


  /* Final 28H — LUUNIMA motion language.
     Animate visual compositions as one unit (frame + image), not their inner parts. */
  const initLuunimaMotion = (scope = document) => {
    const root = scope instanceof Element || scope instanceof Document ? scope : document;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sections = [...root.querySelectorAll('.luunima-section')];

    sections.forEach((section, sectionIndex) => {
      if (section.matches('.luunima-hero, [data-luunima-hero]') || sectionIndex === 0) return;

      const candidates = [];
      const add = (el, type = 'text') => {
        if (!el || el.dataset.luunimaMotionInitialized === 'true') return;
        if (el.closest('[data-luunima-ritual]')) return;
        el.dataset.luunimaMotionInitialized = 'true';
        el.dataset.luunimaMotion = type;
        candidates.push(el);
      };

      /* Friendbook: keep the album itself completely static; only the intro copy may reveal. */
      if (section.matches('.luunima-friendbook-home')) {
        section.querySelectorAll('.luunima-themenwelten__head > *').forEach((el) => add(el, 'text'));
        section.querySelectorAll('.luunima-friendbook-home__page, .luunima-friendbook-home__page *').forEach((el) => {
          el.removeAttribute('data-luunima-motion-initialized');
          el.removeAttribute('data-luunima-motion');
          el.classList.remove('luunima-motion-in');
        });
      /* Insights: image + its crop/frame appear as one composition. Captions stay readable. */
      } else if (section.matches('.luunima-iq')) {
        section.querySelectorAll('.luunima-iq__head > *').forEach((el) => add(el, 'text'));
        section.querySelectorAll('.luunima-iq__imagewrap').forEach((el) => add(el, 'composition'));
      /* Waitlist: keep the box and copy still; only the advantage price gets a soft reveal. */
      } else if (section.matches('.luunima-final--combined')) {
        [...section.querySelectorAll(':scope > .luunima-container > .luunima-eyebrow, :scope > .luunima-container > .luunima-heading, :scope > .luunima-container > .luunima-copy, :scope > .luunima-container > .luunima-link')]
          .forEach((el) => add(el, 'text'));
        /* Price uses its own later trigger so the reveal is actually visible. */
      } else {
        /* Book hero image: animate the visual wrapper so halo/frame and book stay together. */
        const bookVisual = section.querySelector('.luunima-book__visual');
        if (bookVisual) add(bookVisual, 'composition');

        [...section.querySelectorAll('img:not([data-luunima-no-fade]), .luunima-eyebrow, h2, h3, p, .luunima-link, .luunima-button')]
          .filter((el) => !bookVisual || !bookVisual.contains(el))
          .forEach((el) => add(el, el.tagName === 'IMG' ? 'image' : 'text'));
      }

      candidates.forEach((el, index) => {
        el.style.setProperty('--luunima-motion-delay', `${Math.min(index % 4, 3) * 45}ms`);
      });

      /* Book section: let the book settle in first, then bring in the copy. */
      if (section.querySelector('.luunima-book__visual')) {
        const visual = section.querySelector('.luunima-book__visual');
        if (visual?.dataset.luunimaMotionInitialized === 'true') visual.style.setProperty('--luunima-motion-delay', '0ms');
        candidates.filter((el) => el !== visual).forEach((el, index) => {
          el.style.setProperty('--luunima-motion-delay', `${240 + Math.min(index, 4) * 55}ms`);
        });
      }

      if (reduced || !('IntersectionObserver' in window)) {
        candidates.forEach((el) => el.classList.add('luunima-motion-in'));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('luunima-motion-in');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.10, rootMargin: '0px 0px -18% 0px' });

      candidates.forEach((el) => observer.observe(el));
    });

    /* Waitlist price: reveal later in the viewport, independently from the static box. */
    root.querySelectorAll('.luunima-final__advantage[data-luunima-price-reveal]').forEach((price) => {
      if (price.dataset.luunimaPriceInitialized === 'true') return;
      price.dataset.luunimaPriceInitialized = 'true';
      if (reduced || !('IntersectionObserver' in window)) {
        price.classList.add('luunima-price-in');
        return;
      }
      const priceObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('luunima-price-in');
          priceObserver.unobserve(entry.target);
        });
      }, { threshold: 0.30, rootMargin: '0px 0px -28% 0px' });
      priceObserver.observe(price);
    });
  };

  const initRitualHotspots = (scope = document) => {
    const groups = [...scope.querySelectorAll('[data-luunima-hotspots]')].filter((group) => !group.dataset.luunimaHotspotsInitialized);
    groups.forEach((group) => {
      group.dataset.luunimaHotspotsInitialized = 'true';
      const triggers = [...group.querySelectorAll('[data-luunima-hotspot]')];
      const notes = [...group.querySelectorAll('[data-luunima-hotspot-note]')];
      const closeButtons = [...group.querySelectorAll('[data-luunima-hotspot-close]')];
      const closeAll = () => {
        notes.forEach((note) => { note.dataset.open = 'false'; });
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      };
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const noteId = trigger.getAttribute('aria-controls');
          const note = noteId ? document.getElementById(noteId) : null;
          if (!note || !group.contains(note)) return;
          const willOpen = note.dataset.open !== 'true';
          closeAll();
          if (willOpen) {
            note.dataset.open = 'true';
            trigger.setAttribute('aria-expanded', 'true');
            if (window.matchMedia('(max-width: 749px)').matches) {
              window.requestAnimationFrame(() => note.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' }));
            }
          }
        });
      });
      closeButtons.forEach((button) => button.addEventListener('click', closeAll));
      group.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeAll(); });
    });
  };


  const initStoreHeader = (scope = document) => {
    const header = scope.querySelector?.('[data-luunima-store-header]') || document.querySelector('[data-luunima-store-header]');
    if (!header || header.dataset.luunimaHeaderInitialized === 'true') return;
    header.dataset.luunimaHeaderInitialized = 'true';

    const scrollRoot = document.querySelector('.page-wrapper');
    const getScrollTop = () => {
      const rootScroll = scrollRoot instanceof HTMLElement ? scrollRoot.scrollTop : 0;
      return Math.max(rootScroll, window.scrollY || document.documentElement.scrollTop || 0);
    };
    let ticking = false;
    const update = () => {
      ticking = false;
      header.classList.toggle('is-scrolled', getScrollTop() > 20);
      document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
      document.body.style.setProperty('--header-group-height', `${header.offsetHeight}px`);
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive:true });
    if (scrollRoot instanceof HTMLElement) scrollRoot.addEventListener('scroll', requestUpdate, { passive:true });
    window.addEventListener('resize', requestUpdate, { passive:true });

    const desktopDropdowns = [...header.querySelectorAll('.luunima-store-header__dropdown')];
    document.addEventListener('pointerdown', (event) => {
      if (!(event.target instanceof Element)) return;
      desktopDropdowns.forEach((details) => { if (!details.contains(event.target)) details.removeAttribute('open'); });
    });
    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      header.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    });
    header.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
      header.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    }));
    update();
  };

  const initialize = (scope = document) => {
    const root = scope instanceof Element || scope instanceof Document ? scope : document;
    root.querySelectorAll('[data-luunima-ritual]').forEach(initRitual);
    initThemenweltenReveal(root);
    initCompanionReveal(root);
    initLuunimaMotion(root);
    initRitualHotspots(root);
    initStoreHeader(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* LUUNIMA V49 — touch halo without hijacking navigation.
   Important for Shopify theme preview: allow Shopify/browser to perform the native link navigation,
   otherwise preview context can fall back to the published/older theme. */
(() => {
  const coarsePointer = window.matchMedia('(hover: none), (pointer: coarse)');
  const selector = 'a.luunima-button, a.luunima-link, a.luunima-button--gold';

  const showTapGlow = (link) => {
    link.classList.remove('is-tap-glow');
    // Force a reflow so repeated taps can restart the visual feedback.
    void link.offsetWidth;
    link.classList.add('is-tap-glow');
    window.setTimeout(() => link.classList.remove('is-tap-glow'), 500);
  };

  document.addEventListener('pointerdown', (event) => {
    if (!coarsePointer.matches) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest(selector);
    if (!link) return;
    if (event.pointerType === 'mouse') return;
    showTapGlow(link);
    // Deliberately do NOT call preventDefault() and do NOT navigate manually.
    // Native navigation preserves Shopify's preview-bar/theme context.
  }, { passive: true });
})();


/* LUUNIMA V50 — preserve unpublished theme preview across native internal navigation. */
(() => {
  const body = document.body;
  if (!body) return;

  const themeId = body.dataset.luunimaThemeId;
  const themeRole = body.dataset.luunimaThemeRole;
  if (!themeId || themeRole === 'main') return;

  const isInternal = (url) => url.origin === window.location.origin;
  const isSkippable = (link) => {
    const href = link.getAttribute('href') || '';
    return !href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:');
  };

  // Make the current history entry explicitly point at the active draft theme.
  // This matters when the browser reconstructs the page after using Back instead of restoring bfcache.
  try {
    const current = new URL(window.location.href);
    if (!current.searchParams.get('preview_theme_id')) {
      current.searchParams.set('preview_theme_id', themeId);
      current.searchParams.set('_fd', '0');
      current.searchParams.set('pb', '0');
      window.history.replaceState(window.history.state, '', current.href);
    }
  } catch (error) {
    console.warn('LUUNIMA preview URL preservation failed', error);
  }

  const decorateInternalLink = (link) => {
    if (!(link instanceof HTMLAnchorElement) || isSkippable(link)) return;
    try {
      const url = new URL(link.href, window.location.href);
      if (!isInternal(url)) return;
      url.searchParams.set('preview_theme_id', themeId);
      url.searchParams.set('_fd', '0');
      url.searchParams.set('pb', '0');
      link.href = url.href;
    } catch (_) {}
  };

  const decorateAll = (root = document) => {
    root.querySelectorAll?.('a[href]').forEach(decorateInternalLink);
  };

  decorateAll();

  // Re-apply after Shopify dynamically hydrates/re-renders sections.
  document.addEventListener('shopify:section:load', (event) => decorateAll(event.target));
  document.addEventListener('shopify:section:reorder', () => decorateAll());

  // Last-moment safety for links inserted dynamically by Horizon components.
  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (link) decorateInternalLink(link);
  }, { capture: true, passive: true });
})();


/* v77.2: delegated controls for friendbook + usage tabs (works after Shopify section reloads) */
document.addEventListener('click', function(e){
  const arrow=e.target.closest('[data-friend-prev],[data-friend-next]');
  if(arrow){e.preventDefault();const root=arrow.closest('[data-luunima-friendbook]');if(!root)return;const pages=[...root.querySelectorAll('[data-friend-page]')];if(!pages.length)return;let i=pages.findIndex(p=>!p.hidden);if(i<0)i=0;i=(i+(arrow.hasAttribute('data-friend-next')?1:-1)+pages.length)%pages.length;pages.forEach((p,x)=>{p.hidden=x!==i;p.classList.toggle('is-active',x===i)});const c=root.querySelector('[data-friend-counter]');if(c)c.textContent=String(i+1).padStart(2,'0')+' / '+String(pages.length).padStart(2,'0');return;}
  const tab=e.target.closest('[data-use-tab]');
  if(tab){e.preventDefault();const root=tab.closest('[data-luunima-use-tabs]');if(!root)return;root.querySelectorAll('[data-use-tab]').forEach(t=>t.setAttribute('aria-selected',String(t===tab)));root.querySelectorAll('[data-use-panel]').forEach(p=>p.hidden=p.dataset.usePanel!==tab.dataset.useTab);}
});

/* Final 28C: mobile swipe for homepage friendbook */
(function(){
  let swipeStart = null;

  function showFriendPage(root, direction){
    const pages=[...root.querySelectorAll('[data-friend-page]')];
    if(!pages.length)return;
    let i=pages.findIndex(p=>!p.hidden);
    if(i<0)i=0;
    i=(i+direction+pages.length)%pages.length;
    pages.forEach((p,x)=>{p.hidden=x!==i;p.classList.toggle('is-active',x===i)});
    const c=root.querySelector('[data-friend-counter]');
    if(c)c.textContent=String(i+1).padStart(2,'0')+' / '+String(pages.length).padStart(2,'0');
  }

  document.addEventListener('touchstart',function(e){
    const root=e.target.closest && e.target.closest('[data-luunima-friendbook]');
    if(!root || !e.touches || e.touches.length!==1)return;
    swipeStart={root:root,x:e.touches[0].clientX,y:e.touches[0].clientY};
  },{passive:true});

  document.addEventListener('touchend',function(e){
    if(!swipeStart || !e.changedTouches || !e.changedTouches.length){swipeStart=null;return;}
    const end=e.changedTouches[0];
    const dx=end.clientX-swipeStart.x;
    const dy=end.clientY-swipeStart.y;
    const root=swipeStart.root;
    swipeStart=null;
    if(Math.abs(dx)<45 || Math.abs(dx)<=Math.abs(dy)*1.15)return;
    showFriendPage(root,dx<0?1:-1);
  },{passive:true});
})();
